import { CognitoUserPool, CognitoUser, AuthenticationDetails, } from 'amazon-cognito-identity-js';
import { decodeJwt } from 'jose';
import { config } from '../config';
/**
 * Cognito configuration from centralized config
 */
const COGNITO_CONFIG = {
    UserPoolId: config.auth.userPoolId,
    ClientId: config.auth.clientId,
};
// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
    UserPoolId: COGNITO_CONFIG.UserPoolId,
    ClientId: COGNITO_CONFIG.ClientId,
});
/**
 * Parse JWT token and extract user profile.
 * Normalizes Cognito token claims to our UserProfile interface.
 */
function parseUserFromToken(idToken) {
    const decoded = decodeJwt(idToken);
    return {
        userId: decoded.sub,
        tenantId: decoded['custom:tenantId'] || 'default',
        email: decoded.email || '',
        name: decoded.name || decoded.email || 'User',
        roles: parseRoles(decoded),
        provider: 'cognito',
    };
}
/**
 * Parse roles from JWT token.
 * Cognito custom attribute: custom:roles (JSON array string or comma-separated)
 * Falls back to 'user' role if not specified.
 */
function parseRoles(decoded) {
    const rolesAttr = decoded['custom:roles'];
    if (!rolesAttr) {
        return ['user']; // Default role
    }
    // Try to parse as JSON array first (e.g., '["employee","manager"]')
    if (rolesAttr.startsWith('[')) {
        try {
            const parsed = JSON.parse(rolesAttr);
            if (Array.isArray(parsed)) {
                return parsed.filter((role) => typeof role === 'string');
            }
        }
        catch (e) {
            console.error('Failed to parse roles as JSON array:', e);
        }
    }
    // Fallback: Parse comma-separated roles (e.g., 'employee,manager')
    return rolesAttr
        .split(',')
        .map((role) => role.trim())
        .filter(Boolean);
}
/**
 * Login with email and password using Cognito.
 * Returns user profile and tokens on success.
 * Throws NewPasswordChallenge if user needs to change password.
 *
 * @throws NewPasswordChallenge if password change required
 * @throws Error if authentication fails
 */
export async function login(credentials) {
    const { email, password } = credentials;
    const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
    });
    const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
    });
    return new Promise((resolve, reject) => {
        cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (session) => {
                const accessToken = session.getAccessToken().getJwtToken();
                const idToken = session.getIdToken().getJwtToken();
                const refreshToken = session.getRefreshToken().getToken();
                // Parse user profile from ID token
                const user = parseUserFromToken(idToken);
                resolve({
                    user,
                    tokens: {
                        accessToken,
                        idToken,
                        refreshToken,
                    },
                });
            },
            onFailure: (err) => {
                console.error('Cognito authentication error:', err);
                reject(new Error(err.message || 'Authentication failed'));
            },
            // Handle MFA if enabled (optional for future)
            mfaRequired: () => {
                reject(new Error('MFA not yet supported'));
            },
            // Handle new password required (first-time login)
            newPasswordRequired: (userAttributes) => {
                // Reject with NewPasswordChallenge so UI can handle it
                const challenge = {
                    type: 'NEW_PASSWORD_REQUIRED',
                    email,
                    cognitoUser,
                    userAttributes: userAttributes || {},
                };
                reject(challenge);
            },
        });
    });
}
/**
 * Complete the new password challenge for a user who needs to change their password.
 * Called after initial authentication when newPasswordRequired is triggered.
 *
 * @param challenge - The challenge object from the login attempt
 * @param newPassword - The new password to set
 * @returns AuthResult with user profile and tokens
 * @throws Error if password change fails
 */
export async function completeNewPasswordChallenge(challenge, newPassword) {
    const { cognitoUser } = challenge;
    return new Promise((resolve, reject) => {
        // Pass empty object for attributes - don't try to set any attributes
        // The user attributes are already set from the backend (AdminCreateUser)
        // We only need to complete the password challenge, not modify attributes
        cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
            onSuccess: (session) => {
                const accessToken = session.getAccessToken().getJwtToken();
                const idToken = session.getIdToken().getJwtToken();
                const refreshToken = session.getRefreshToken().getToken();
                // Parse user profile from ID token
                const user = parseUserFromToken(idToken);
                resolve({
                    user,
                    tokens: {
                        accessToken,
                        idToken,
                        refreshToken,
                    },
                });
            },
            onFailure: (err) => {
                console.error('Password change error:', err);
                reject(new Error(err.message || 'Failed to change password'));
            },
        });
    });
}
/**
 * Logout current user from Cognito.
 * Clears local session and invalidates tokens.
 */
export async function logout() {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
        cognitoUser.signOut();
    }
    // Additional cleanup if needed
    return Promise.resolve();
}
/**
 * Get current authenticated user session.
 * Returns null if no valid session exists.
 */
export async function getCurrentSession() {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
        return null;
    }
    return new Promise((resolve, reject) => {
        cognitoUser.getSession((err, session) => {
            if (err) {
                console.error('Error getting session:', err);
                reject(err);
                return;
            }
            if (!session || !session.isValid()) {
                resolve(null);
                return;
            }
            resolve(session);
        });
    });
}
/**
 * Refresh the current user session using the refresh token.
 * Returns new tokens on success.
 *
 * @throws Error if refresh fails
 */
export async function refreshSession() {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
        throw new Error('No authenticated user found');
    }
    return new Promise((resolve, reject) => {
        cognitoUser.getSession((err, session) => {
            if (err || !session) {
                reject(new Error('Failed to refresh session'));
                return;
            }
            const accessToken = session.getAccessToken().getJwtToken();
            const idToken = session.getIdToken().getJwtToken();
            const refreshToken = session.getRefreshToken().getToken();
            const user = parseUserFromToken(idToken);
            resolve({
                user,
                tokens: {
                    accessToken,
                    idToken,
                    refreshToken,
                },
            });
        });
    });
}
/**
 * Check if Cognito is properly configured.
 * Useful for showing configuration errors in development.
 */
export function isCognitoConfigured() {
    return Boolean(COGNITO_CONFIG.UserPoolId && COGNITO_CONFIG.ClientId);
}
/**
 * Get configuration status for debugging.
 */
export function getCognitoConfig() {
    return {
        isConfigured: isCognitoConfigured(),
        userPoolId: COGNITO_CONFIG.UserPoolId
            ? `${COGNITO_CONFIG.UserPoolId.slice(0, 10)}...`
            : 'NOT_SET',
        clientId: COGNITO_CONFIG.ClientId
            ? `${COGNITO_CONFIG.ClientId.slice(0, 10)}...`
            : 'NOT_SET',
    };
}
/**
 * Change password for the currently authenticated user.
 * Requires the user's current password for verification.
 *
 * @param oldPassword - The user's current password
 * @param newPassword - The new password to set
 * @throws Error if password change fails
 */
export async function changePassword(oldPassword, newPassword) {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
        throw new Error('No authenticated user found. Please log in again.');
    }
    return new Promise((resolve, reject) => {
        // Must get session first to ensure user is authenticated
        cognitoUser.getSession((err, session) => {
            if (err || !session?.isValid()) {
                reject(new Error('Session expired. Please log in again.'));
                return;
            }
            // Now change the password
            cognitoUser.changePassword(oldPassword, newPassword, (err) => {
                if (err) {
                    console.error('Password change error:', err);
                    // Provide user-friendly error messages
                    const errorCode = err.code;
                    if (errorCode === 'NotAuthorizedException') {
                        reject(new Error('Current password is incorrect'));
                    }
                    else if (errorCode === 'InvalidPasswordException') {
                        reject(new Error('New password does not meet requirements'));
                    }
                    else if (errorCode === 'LimitExceededException') {
                        reject(new Error('Too many password change attempts. Please try again later.'));
                    }
                    else {
                        reject(new Error(err.message || 'Failed to change password'));
                    }
                    return;
                }
                resolve();
            });
        });
    });
}
